<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';

    import Answer from './Answer.svelte';
    import ProgressBar from './ProgressBar.svelte';
    import { getBoxAttributes } from '../_utils/getBoxAttributes';
    import Box from '../_wrappers/Box.svelte';
    import Text from '../Text/Text.View.svelte';

    import type { Align, Box as BoxType } from '$lib/types/Components/SharedTypes';
    import type { Language } from '$lib/types/Language';
    import type { TransitionEffect } from '$lib/types/Theme';
    import type { JSONContent } from '@tiptap/core';
    import type { ComponentProps } from 'svelte';

    export let id = '';
    export let src: string;
    export let focalPoint: { x: number; y: number };
    export let align: Align = 'center';
    export let draft: JSONContent;
    export let color = 'black';
    export let box: BoxType;
    export let onAnswerClick: ComponentProps<Answer>['onClick'];
    export let onAnswerMouseEnter: ComponentProps<Answer>['onMouseEnter'];
    export let answers: Omit<ComponentProps<Answer>, 'onClick' | 'onMouseEnter'>[] = [];
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let layout = DEFAULT_LAYOUT;
    export let language: Language = 'en';

    let videoEl: HTMLVideoElement | null = null;
    let progressPercent = 0;
    let playing = false;

    const { isInColumn, isInMultiColumn } = layout;

    const borderRadiusClass = getBorderRadius('videoQuestionFrame');

    const textBox: BoxType = {
        top: 'none',
        right: 'none',
        bottom: 'large',
        left: 'none',
    };

    const handleTimeUpdate = () => {
        if (videoEl && videoEl.duration) {
            progressPercent = (videoEl.currentTime / videoEl.duration) * 100;

            progressPercent = progressPercent;
        }
    };

    const handleVideoClick = () => {
        playing = !playing;

        if (playing) {
            videoEl.currentTime = 0;
        }
    };
</script>

<Box {id} {...getBoxAttributes({ box, isInColumn })} {transitionDelay} {transitionEffect}>
    <div
        class={cn(
            'fun-relative fun-isolate fun-flex fun-aspect-[9/16] fun-flex-col fun-justify-between fun-bg-black',
            borderRadiusClass,
            {
                'md:fun-aspect-auto md:fun-h-[560px] md:fun-flex-row md:fun-justify-normal md:fun-overflow-hidden md:fun-bg-gradient-to-r md:fun-from-neutral-600 md:fun-to-black':
                    !isInMultiColumn,
            },
        )}
    >
        <div
            class={cn('fun-absolute fun-isolate fun-size-full fun-overflow-hidden', {
                'md:fun-relative md:fun-flex md:fun-aspect-[9/16] md:fun-size-auto md:fun-h-full md:fun-w-auto':
                    !isInMultiColumn,
            })}
        >
            <div
                class={cn(
                    'fun-absolute fun-inset-0 fun-z-10 fun-hidden fun-h-20 fun-bg-gradient-to-t fun-from-transparent fun-to-black/30 fun-p-4',
                    borderRadiusClass,
                    { 'md:fun-block': !isInMultiColumn },
                )}
            >
                <ProgressBar progress={progressPercent} />
            </div>

            <!-- Play/Pause button -->
            <div
                on:click={handleVideoClick}
                role="button"
                tabindex="0"
                on:keydown={(evt) => {
                    if (evt.key === 'Enter' || evt.key === ' ') {
                        evt.preventDefault();
                        handleVideoClick();
                    }
                }}
                class="fun-absolute fun-inset-0 fun-flex fun-size-full fun-items-center fun-justify-center"
                aria-label={language === 'de' ? 'Video abspielen' : 'Play video'}
            >
                {#if !playing}
                    <div
                        class="fun-flex fun-size-16 fun-items-center fun-justify-center fun-rounded-full fun-border fun-border-neutral-500/50 fun-bg-black/50 fun-text-white fun-backdrop-blur-sm"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            class="fun-size-8"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </div>
                {/if}
            </div>
            <video
                {src}
                muted={!playing}
                class={cn(
                    'fun-absolute fun-left-1/2 fun-top-1/2 fun--z-[1] fun-min-h-full fun-min-w-full fun--translate-x-1/2 fun--translate-y-1/2 fun-object-cover',
                    borderRadiusClass,
                )}
                style="object-position: {focalPoint.x}% {focalPoint.y}%;"
                on:timeupdate={handleTimeUpdate}
                bind:this={videoEl}
                autoPlay
                loop
                playsInline
            />
        </div>

        <!-- Mobile -->
        <div
            class={cn(
                'fun-z-10 fun-bg-gradient-to-t fun-from-transparent fun-to-black/30 fun-p-4',
                borderRadiusClass,
                {
                    'md:fun-hidden': !isInMultiColumn,
                },
            )}
        >
            <ProgressBar progress={progressPercent} />
            <div class="fun-mt-2">
                <Text {transitionDelay} raw={draft} {align} {color} box={textBox} {layout} />
            </div>
        </div>

        <div
            class={cn(
                'fun-z-10 fun-flex fun-flex-col fun-gap-2 fun-bg-gradient-to-b fun-from-transparent fun-to-black fun-p-2',
                borderRadiusClass,
                {
                    'md:fun-flex-1 md:fun-items-center md:fun-justify-center md:fun-gap-4 md:fun-to-transparent md:fun-p-0':
                        !isInMultiColumn,
                },
            )}
        >
            <div
                class={cn('fun-flex fun-flex-col fun-gap-2', {
                    'md:fun-w-96 md:fun-gap-4': !isInMultiColumn,
                })}
            >
                <div class={cn('fun-hidden', { 'md:fun-block': !isInMultiColumn })}>
                    <Text {transitionDelay} raw={draft} {align} {color} box={textBox} {layout} />
                </div>
                {#each answers as answer (answer.id)}
                    <Answer {...answer} onClick={onAnswerClick} onMouseEnter={onAnswerMouseEnter} />
                {/each}
            </div>
        </div>
    </div>
</Box>
